import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEnrollment } from '../contexts/EnrollmentContext';
import {
  Play,
  Clock,
  Calendar,
  BookOpen,
  CheckCircle,
  TrendingUp,
  Award,
} from 'lucide-react';

const MyCourses = () => {
  const { getActiveEnrollments, getCompletedEnrollments, enrollments, loading } = useEnrollment();
  const [activeTab, setActiveTab] = useState('active');

  const activeEnrollments = getActiveEnrollments();
  const completedEnrollments = getCompletedEnrollments();

  // Stats calculées localement
  const stats = {
    total: enrollments.length,
    active: activeEnrollments.length,
    completed: completedEnrollments.length,
    averageProgress: enrollments.length > 0
      ? Math.round(enrollments.reduce((s, e) => s + (Number(e.progress) || 0), 0) / enrollments.length)
      : 0,
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:          return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':    return 'En cours';
      case 'completed': return 'Terminé';
      default:          return 'Inscrit';
    }
  };

  const getCourseId = (e) => e.course_id || e.courseId;
  const getCourseTitle = (e) => e.course?.title || e.courseTitle || e.title || 'Cours';
  const getCourseImage = (e) => e.course?.thumbnail || e.courseImage || null;
  const getInstructor = (e) => {
    const raw = e.course?.instructor || e.instructor;
    if (!raw) return '';
    if (typeof raw === 'object') return `${raw.first_name || ''} ${raw.last_name || ''}`.trim();
    return raw;
  };
  const getEnrolledAt = (e) => e.enrolled_at || e.enrolledAt || '';
  const getLastAccessed = (e) => e.last_accessed || e.lastAccessed || e.enrolled_at || '';

  const CourseCard = ({ enrollment }) => {
    const progress = Number(enrollment.progress) || 0;
    const courseId = getCourseId(enrollment);
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
        <div className="relative">
          {getCourseImage(enrollment) ? (
            <img
              src={getCourseImage(enrollment)}
              alt={getCourseTitle(enrollment)}
              className="w-full h-40 object-cover"
            />
          ) : (
            <div className="w-full h-40 bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-primary/40" />
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(enrollment.status)}`}>
              {getStatusText(enrollment.status)}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs font-medium">
              {progress}%
            </span>
          </div>
        </div>

        <div className="p-5">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
            {getCourseTitle(enrollment)}
          </h3>
          {getInstructor(enrollment) && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Par {getInstructor(enrollment)}
            </p>
          )}

          {/* Progression */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
              <span>Progression</span><span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all"
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-1 mb-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Calendar className="w-3.5 h-3.5" />
              <span>Inscrit le {formatDate(getEnrolledAt(enrollment))}</span>
            </div>
            {getLastAccessed(enrollment) && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Vu le {formatDate(getLastAccessed(enrollment))}</span>
              </div>
            )}
          </div>

          <Link
            to={`/course/${courseId}/learn`}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm"
          >
            <Play className="w-4 h-4" />
            {enrollment.status === 'completed' ? 'Revoir' : 'Continuer'}
          </Link>
        </div>
      </div>
    );
  };

  if (loading && enrollments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement de vos cours…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Mes Cours</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gérez vos cours et suivez votre progression.
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: <BookOpen className="w-7 h-7 text-blue-500" />, value: stats.total,           label: 'Cours total' },
          { icon: <Play      className="w-7 h-7 text-green-500" />, value: stats.active,          label: 'En cours' },
          { icon: <CheckCircle className="w-7 h-7 text-purple-500" />, value: stats.completed,   label: 'Terminés' },
          { icon: <TrendingUp  className="w-7 h-7 text-orange-500" />, value: `${stats.averageProgress}%`, label: 'Progression moy.' },
        ].map((s, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 text-center">
            <div className="flex justify-center mb-2">{s.icon}</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{s.value}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Onglets */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Play className="w-4 h-4" /> En cours ({activeEnrollments.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <CheckCircle className="w-4 h-4" /> Terminés ({completedEnrollments.length})
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'active' && (
            activeEnrollments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeEnrollments.map(e => <CourseCard key={e.id} enrollment={e} />)}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Aucun cours en cours</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Commencez votre apprentissage en vous inscrivant à un cours.
                </p>
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90"
                >
                  <BookOpen className="w-4 h-4" /> Explorer les cours
                </Link>
              </div>
            )
          )}
          {activeTab === 'completed' && (
            completedEnrollments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedEnrollments.map(e => <CourseCard key={e.id} enrollment={e} />)}
              </div>
            ) : (
              <div className="text-center py-12">
                <Award className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Aucun cours terminé</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Terminez vos cours et validez toutes les évaluations pour les voir ici.
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCourses;
